import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useSearchCustomers } from '../../../services/customerService';
import type { SearchCustomersParams } from '../../../services/schemas/customerServiceSchemas';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../utils/rolePermissions';
import { CustomerBalanceCell } from './LoanCustomers';
import IndividualCustomerActions from './IndividualCustomerActions';
import { customerError, individualName, individualStatusLabel, individualStatusVariant, IndividualCustomer, isIndividualCustomer } from './individualCustomer';
import './LoanCustomers.css';

const emptySearch = { customerNumber: '', name: '', idNumber: '', status: '', createdDateFrom: '', createdDateTo: '' };
function CustomerCount({ status }: { status?: SearchCustomersParams['status'] }) {
  const query = useSearchCustomers({ customerType: 'INDIVIDUAL', status, page: 0, size: 1 }, { query: { staleTime: 30000 } });
  const suspended = useSearchCustomers({ customerType: 'INDIVIDUAL', status: 'SUSPENDED', page: 0, size: 1 }, {
    query: { enabled: status === 'BLACKLISTED', staleTime: 30000 }
  });
  if (query.isError || (status === 'BLACKLISTED' && suspended.isError)) return <>—</>;
  if (query.isLoading) return <>…</>;
  return <>{(query.data?.totalElements ?? 0) + (status === 'BLACKLISTED' ? suspended.data?.totalElements ?? 0 : 0)}</>;
}

export default function IndividualCustomers() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [showSearch, setShowSearch] = useState(false);
  const [form, setForm] = useState(emptySearch);
  const [filters, setFilters] = useState<SearchCustomersParams>({});
  const [page, setPage] = useState(0), [size, setSize] = useState(10), [resetPage, setResetPage] = useState(false);
  const [balanceMap, setBalanceMap] = useState<Record<string, number>>({});
  const setBalanceForCustomer = useCallback((customerNumber: string, amount: number) => {
    setBalanceMap(previous => previous[customerNumber] === amount ? previous : { ...previous, [customerNumber]: amount });
  }, []);
  const query = useSearchCustomers({ ...filters, customerType: 'INDIVIDUAL', page, size });
  const customers = (query.data?.content ?? []).filter(isIndividualCustomer);
  const statusIcons: Record<string, string> = { ACTIVE: 'fa-check-circle', INACTIVE: 'fa-times-circle', SUSPENDED: 'fa-ban', BLACKLISTED: 'fa-ban' };
  const columns: TableColumn<IndividualCustomer>[] = [
    {
      name: 'Customer Number', selector: customer => customer.customerNumber || '', sortable: true,
      cell: customer => <div className="d-flex align-items-center">
        <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
          <i className="fa fa-hashtag text-primary" style={{ display: 'block', fontSize: '0.85rem' }} />
        </div>
        <Link className="fw-semibold text-primary" to={`/individual-customers/${customer.id}/view`}>{customer.customerNumber || 'N/A'}</Link>
      </div>, minWidth: '160px', maxWidth: '180px'
    },
    {
      name: 'Full Name', selector: individualName, sortable: true,
      cell: customer => <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>{individualName(customer)}</div>,
      minWidth: '250px', wrap: true
    },
    {
      name: 'ID / Passport Number', selector: customer => customer.info.idNumber, sortable: true,
      cell: customer => customer.info.idNumber ? <span className="badge bg-light text-dark border px-2 py-1">{customer.info.idNumber}</span> : <span className="text-muted">N/A</span>,
      minWidth: '180px'
    },
    {
      name: 'Balance', selector: customer => customer.customerNumber || '', sortable: true,
      sortFunction: (a, b) => (balanceMap[a.customerNumber || ''] ?? 0) - (balanceMap[b.customerNumber || ''] ?? 0),
      cell: customer => <CustomerBalanceCell customerNumber={customer.customerNumber} onBalance={setBalanceForCustomer} />, minWidth: '160px'
    },
    {
      name: 'Status', selector: customer => individualStatusLabel(customer.status), sortable: true,
      cell: customer => <Badge bg={individualStatusVariant(customer.status)} className="px-3 py-2">
        <i className={`fa ${statusIcons[customer.status || ''] || 'fa-circle'} me-1`} />{individualStatusLabel(customer.status)}
      </Badge>, minWidth: '120px', maxWidth: '150px'
    },
    {
      name: 'Actions', cell: customer => <div className="d-flex align-items-center gap-2">
        {hasPermission(Permission.VIEW_CUSTOMER_DOCUMENTS) && <Link className="btn btn-sm btn-info rounded-circle" title="Documents" aria-label={`Documents for ${individualName(customer)}`}
          style={{ width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} to={`/individual-customers/${customer.id}/documents`}><i className="fa fa-file" /></Link>}
        <IndividualCustomerActions customer={customer} />
      </div>, ignoreRowClick: true, minWidth: '220px', allowOverflow: true
    }
  ];
  const reset = () => { setForm(emptySearch); setFilters({}); setPage(0); setResetPage(value => !value); };
  const errorAlert = query.isError && <Alert variant="danger" className="mb-0 py-2">
    <i className="fa fa-exclamation-circle me-2" />{customerError(query.error, 'Unable to load individual customers')}
    <Button variant="link" onClick={() => query.refetch()}>Retry</Button>
  </Alert>;
  return <>
    <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
            <div className="row g-3 mb-3 align-items-center">
                <div className="col">
                    <ol className="breadcrumb bg-transparent mb-0">
                        <li className="breadcrumb-item">
                            <Link className="text-white" to="/">
                                <i className="fa fa-home me-2"></i>Home
                            </Link>
                        </li>
                        <li className="breadcrumb-item active text-white" aria-current="page">Individual Customers</li>
                    </ol>
                </div>
            </div>
            <div className="row align-items-center">
                <div className="col-md-6">
                    <h1 className="fs-2 fw-bold mb-2 text-white">
                        Individual Customers
                    </h1>
                    <p className="mb-0 text-white fs-6 opacity-75">
                        Manage and search for individual customer records
                    </p>
                </div>
                <div className="col-md-6 text-md-end">
                    <div className="d-flex gap-3 justify-content-md-end flex-wrap align-items-center">
                        <Button
                            variant="light"
                            className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                            style={{
                                backgroundColor: '#ffffff',
                                color: '#495057',
                                border: '2px solid rgba(255, 255, 255, 0.8)',
                                transition: 'all 0.3s ease',
                                fontSize: '0.9rem',
                                minWidth: '140px',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#ffffff';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.25)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#ffffff';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
                            }}
                            onClick={() => setShowSearch(!showSearch)}
                            aria-expanded={showSearch}
                            aria-controls="individual-customer-search"
                        >
                            <i className={`fa ${showSearch ? 'fa-times' : 'fa-search'} me-2`} style={{ fontSize: '0.85rem' }}></i>
                            {showSearch ? 'Hide Search' : 'Advanced Search'}
                        </Button>
                        {hasPermission(Permission.CREATE_APPLICATION) && <Button
                            variant="info"
                            className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                            style={{
                                backgroundColor: '#ffffff',
                                color: '#17a2b8',
                                border: '2px solid #17a2b8',
                                transition: 'all 0.3s ease',
                                fontSize: '0.9rem',
                                minWidth: '140px',
                                boxShadow: '0 6px 20px rgba(23, 162, 184, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#17a2b8';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(23, 162, 184, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#ffffff';
                                e.currentTarget.style.color = '#17a2b8';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(23, 162, 184, 0.3)';
                            }}
                            onClick={() => navigate('/loan-application/new', { state: { customerType: 'individual' } })}
                        >
                            <i className="fa fa-file-text me-2" style={{ fontSize: '0.85rem' }}></i>
                            New Application
                        </Button>}
                        {hasPermission(Permission.CREATE_CUSTOMER) && <Button
                            variant="success"
                            className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                            style={{
                                backgroundColor: '#ffffff',
                                color: '#28a745',
                                border: '2px solid #28a745',
                                transition: 'all 0.3s ease',
                                fontSize: '0.9rem',
                                minWidth: '140px',
                                boxShadow: '0 6px 20px rgba(40, 167, 69, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#28a745';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(40, 167, 69, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#ffffff';
                                e.currentTarget.style.color = '#28a745';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.3)';
                            }}
                            onClick={() => navigate('/individual-customers/new')}
                        >
                            <i className="fa fa-plus-circle me-2" style={{ fontSize: '0.85rem' }}></i>
                            Add Customer
                        </Button>}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3"><div className="container-fluid">
      <div className="row g-4 mb-4">
          <div className="col-lg-3 col-md-6">
              <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                          <div>
                              <p className="text-muted small mb-1">Total Customers</p>
                              <h3 className="mb-0 fw-bold text-primary"><CustomerCount /></h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                              <i className="fa fa-user fa-2x text-primary" style={{ display: 'block' }}></i>
                          </div>
                      </div>
                  </Card.Body>
              </Card>
          </div>
          <div className="col-lg-3 col-md-6">
              <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                          <div>
                              <p className="text-muted small mb-1">Active Customers</p>
                              <h3 className="mb-0 fw-bold text-success"><CustomerCount status="ACTIVE" /></h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(40,167,69,0.2)' }}>
                              <i className="fa fa-check-circle fa-2x text-success" style={{ display: 'block' }}></i>
                          </div>
                      </div>
                  </Card.Body>
              </Card>
          </div>
          <div className="col-lg-3 col-md-6">
              <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                          <div>
                              <p className="text-muted small mb-1">Inactive Customers</p>
                              <h3 className="mb-0 fw-bold text-danger"><CustomerCount status="INACTIVE" /></h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(220,53,69,0.2)' }}>
                              <i className="fa fa-times-circle fa-2x text-danger" style={{ display: 'block' }}></i>
                          </div>
                      </div>
                  </Card.Body>
              </Card>
          </div>
          <div className="col-lg-3 col-md-6">
              <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between">
                          <div>
                              <p className="text-muted small mb-1">Blacklisted Customers</p>
                              <h3 className="mb-0 fw-bold text-warning"><CustomerCount status="BLACKLISTED" /></h3>
                          </div>
                          <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                              <i className="fa fa-exclamation-triangle fa-2x text-warning" style={{ display: 'block' }}></i>
                          </div>
                      </div>
                  </Card.Body>
              </Card>
          </div>
      </div>
      {showSearch && <div className="row mb-4" id="individual-customer-search"><div className="col-12"><Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 py-3"><h5 className="mb-0 fw-bold"><i className="fa fa-search text-primary me-2" />Advanced Search</h5></Card.Header>
        <Card.Body>
          {query.isError && <div className="mb-4">{errorAlert}</div>}
          <Form onSubmit={event => {
            event.preventDefault();
            setFilters(Object.fromEntries(Object.entries(form).filter(([, value]) => value.trim()).map(([key, value]) => [key, value.trim()])) as SearchCustomersParams);
            setPage(0); setResetPage(value => !value);
          }}><div className="row g-3">
            {([['customerNumber', 'Customer Number'], ['name', 'Full Name'], ['idNumber', 'ID / Passport Number'], ['createdDateFrom', 'Created Date From'], ['createdDateTo', 'Created Date To']] as const).map(([key, label]) =>
              <Form.Group className="col-md-4" controlId={`individual-search-${key}`} key={key}><Form.Label className="fw-semibold">{label}</Form.Label><Form.Control type={key.startsWith('created') ? 'date' : 'text'} value={form[key]} onChange={event => setForm(value => ({ ...value, [key]: event.target.value }))} /></Form.Group>)}
            <Form.Group className="col-md-4" controlId="individual-search-status"><Form.Label className="fw-semibold">Status</Form.Label><Form.Select value={form.status} onChange={event => setForm(value => ({ ...value, status: event.target.value }))}><option value="">All</option>{['ACTIVE', 'INACTIVE', 'SUSPENDED', 'BLACKLISTED'].map(status => <option key={status} value={status}>{status}</option>)}</Form.Select></Form.Group>
            <div className="col-12"><div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={reset}><i className="fa fa-undo me-2" />Reset</Button>
              <Button type="submit"><i className="fa fa-search me-2" />Search</Button>
            </div></div>
          </div></Form>
        </Card.Body>
      </Card></div></div>}
      <div className="row"><div className="col-12"><Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 py-3 px-4"><div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold d-flex align-items-center"><i className="fa fa-user text-primary me-2" />Individual Customers
            {(query.data?.totalElements ?? 0) > 0 && <Badge bg="primary" className="ms-2">{query.data?.totalElements}</Badge>}
          </h5>
          {!showSearch && errorAlert}
        </div></Card.Header>
        <Card.Body className="p-0">
          {query.isLoading ? <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}><span className="visually-hidden">Loading...</span></div>
            <p className="mt-3 text-muted">Loading customers...</p>
          </div> : <div className="table-responsive"><DataTable columns={columns} data={customers} highlightOnHover striped responsive pagination paginationServer
            paginationTotalRows={query.data?.totalElements ?? 0} paginationPerPage={size} paginationResetDefaultPage={resetPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]} onChangePage={value => setPage(value - 1)}
            onChangeRowsPerPage={value => { setSize(value); setPage(0); setResetPage(current => !current); }}
            fixedHeader fixedHeaderScrollHeight="calc(100vh - 400px)"
            paginationComponentOptions={{ rowsPerPageText: 'Rows per page:', rangeSeparatorText: 'of', selectAllRowsItem: true, selectAllRowsItemText: 'All' }}
            noDataComponent={<div className="text-center py-5">
              <i className="fa fa-user fa-3x text-muted mb-3" /><h5 className="text-muted">No Customers Found</h5>
              <p className="text-muted mb-0">{Object.keys(filters).length ? 'Try adjusting your search criteria' : 'No individual customers available at this time'}</p>
            </div>}
            customStyles={{
                table: {
                    style: {
                        backgroundColor: 'white',
                    },
                },
                headRow: {
                    style: {
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    },
                },
                headCells: {
                    style: {
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                    },
                },
                cells: {
                    style: {
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        fontSize: '0.85rem',
                    },
                },
                rows: {
                    style: {
                        minHeight: '55px',
                        borderBottom: '1px solid #f8f9fa',
                    },
                    stripedStyle: {
                        backgroundColor: '#ffffff',
                    },
                },
            }}
          /></div>}
        </Card.Body>
      </Card></div></div>
    </div></div>
  </>;
}
